// ─── SommSync Theme System ────────────────────────────────────────────────────
//
// This file owns every colour and font in the app. App.js contains no hex values
// and no font stacks — if you find one there, it belongs here. The one exception
// is SAFE_THEME_FLOOR at the top of App.js, which is the literal last resort for
// when this file cannot be read at all.
//
// A "token" is one named slot below — `accent`, `textPrimary`, `borderMid`. Every
// scheme fills in every slot, and App.js only ever names the slot. That is the
// whole mechanism: changing scheme changes what `t.accent` resolves to, and the
// screen follows. A colour written into App.js can never do that, which is why
// nothing there is a hex.
//
// ADDING A SCHEME
//   1. Copy an existing block below and give it a new key
//   2. Fill in every token — a missing one renders as `undefined` and the element
//      loses its background or text colour silently. Copying a whole existing
//      block and editing the values is safer than writing one from memory
//   3. Register it in THEME_CATALOGUE at the bottom so it appears in Settings
//
// LIGHT VS DARK
//   `white04` / `white06` / `white08` are subtle surface overlays. On dark schemes
//   they are white at low alpha; on light schemes they must be inverted to dark at
//   low alpha, or every panel disappears into the background.
//
// TOKEN GROUPS
//   accentSecondary / Dim / Border — a second brand colour, used for the grape
//     and region line under a wine name and for the wine filter pills. Defaults
//     to the scheme's own `accent` so a scheme reads as single-accent unless it
//     deliberately sets a second one. Black & Cherry is the one that does.
//   (There are deliberately NO per-category colours. The beer, spirits, cocktail
//     and non-alcoholic screens used to carry their own amber/tobacco/plum/slate
//     accent. A per-category hue is a colour the scheme cannot reach, which is
//     why a red room still showed a plum cocktail filter. Every list screen now
//     uses accent and accentSecondary like the wine screen does.)
//   info / infoDim / infoBorder — the manager-side "re-enrich" and "search
//     again" blue. Was a fixed semantic colour; now per-scheme.
//   onDeepText / onDeepTextDim — text that sits ON a bgDeep band (the course
//     header bars on the pairing screen: "STARTERS", "1ST", "MAIN"). On
//     dark-dominant schemes bgDeep is darker than the body, so accent and
//     textSecondary already read on it and these simply mirror them. On a
//     light-dominant scheme whose bgDeep is actually a dark band (then: Cobalt &
//     White, Olive & Linen, Terracotta & Chalk, Emerald & Cream, Ink & Paper;
//     Emerald & Cream has since gone dark),
//     accent and textSecondary were chosen for the light body and measured
//     under 3:1 against that band - unreadable, which is exactly what
//     happened on Emerald & Cream's "STARTERS / 1ST / MAIN" bar. Those five
//     get real light values here instead, each checked at 7:1+ against their
//     own bgDeep. Parchment & Maple's bgDeep is not actually dark (#e4ddce,
//     same family as its body) so its accent already reads fine and is left
//     alone. This is a separate slot, not a change to accent, so the brand
//     colour stays exact everywhere else.
//   courseBandBg — the actual background of that same course-header band,
//     separate from bgDeep (which still does its original job: the Print
//     modal backdrop, the label-zoom overlay, the Settings "Device Setup"
//     header). bgDeep was designed to go very dark regardless of hue, so the
//     band read as "basically black" on every scheme no matter the brand
//     colour. courseBandBg is each scheme's own accent blended toward black
//     just far enough to hold 6:1 against white band text, so the colour is
//     actually visible rather than theoretical - a warm brown for Espresso &
//     Brass, a deep teal for Emerald & Platinum, a true olive for Olive &
//     Linen, instead of the same near-black everywhere. Ink & Paper's accent
//     is already black, so it ends up unchanged - consistent with that
//     scheme's whole premise.
//   warningBorder — the border partner warningDim never had.
//   labelBackdrop — behind bottle label photos. Cream in every scheme today,
//     because labels are shot on white and a dark backdrop makes them look cut
//     out, but it is a token now so a scheme can diverge.
//   shadow / shadowStrong — drop shadows. Not decorative: an 0.7 black under a
//     panel disappears on espresso and looks like a bruise on parchment, so the
//     light schemes carry much weaker values.
//
// COLOUR ACCURACY OVER CONTRAST MATH
//   `accent` is always the true, exact brand colour as briefed — nothing in this
//   file is desaturated or lightened to hit a contrast ratio. WCAG-style
//   legibility floors (e.g. 4.5:1 for small text) are NOT a constraint here by
//   design decision; matching the intended colour takes priority. If a specific
//   pairing ever reads as hard to see in practice, that's a case-by-case call to
//   revisit deliberately — not something to solve by quietly shifting the hex.
//
// DOMINANT VS SECONDARY BACKGROUND
//   Two shapes in this file, both intentional:
//   - Dark-dominant (espressoAndBrass, blackAndCherry, slateAndChrome,
//     emeraldAndCream, onyxAndChalk): bgBase is the dark tone, bgDeep is darker
//     still (recessed surfaces), accent is a saturated colour (or, in
//     onyxAndChalk and slateAndChrome, plain white) popping against dark.
//   - Light-dominant (parchmentAndMaple, cobaltAndWhite, oliveAndLinen,
//     terracottaAndChalk, inkAndPaper): bgBase is the light tone, bgDeep is
//     reserved for a dark header/hero band, accent is a saturated colour on the
//     light body.
//   Every scheme declares which it is via `isLight`. That is not decoration:
//   HomeScreen uses it to decide whether to brightness-boost an uploaded logo,
//   since a white logo file needs the lift on a dark field and looks blown out
//   on a light one. A new scheme that omits `isLight` will be treated as dark.
//   Which one a new scheme should be is usually obvious from its name — "Black
//   & X" reads dark-dominant the same way "X & Cream/Chalk/Linen/White" reads
//   light-dominant. When it's not obvious, ask rather than guess.
//
// PURE-WHITE BASES
//   cobaltAndWhite, oliveAndLinen and inkAndPaper all sit on #ffffff. On those
//   three, `bgSurface` equals `bgBase` — there is nothing lighter than white to
//   raise a panel to, so separation comes from borderSubtle and shadow instead
//   of a tonal step. That is deliberate; do not "fix" it by tinting bgBase off
//   white, which is exactly the yellow cast these three were moved away from.
//
// THE LIBRARY, AT A GLANCE
//   Warm dark    — espressoAndBrass (brown/brass), blackAndCherry (black/red)
//   Cool dark    — slateAndChrome (navy/white, shown as "Navy & White"; it
//                  was steel grey until the key was repurposed), emeraldAndCream
//                  (emerald/platinum), onyxAndChalk (black/white)
//   Warm light   — parchmentAndMaple, terracottaAndChalk
//   Crisp light  — cobaltAndWhite (blue), oliveAndLinen (green), inkAndPaper (mono)
//   The gap this pass closed was a cool dark: every dark scheme used to be warm,
//   so a modern seafood or late-night cocktail room had nothing to pick. It also
//   retired blackAndOchre, whose dark base plus warm gold accent was close enough
//   to espressoAndBrass that choosing between them was not a real choice.
//
// 2026 REFRESH — this file replaces the previous theme set wholesale. If a
// restaurant's Settings still points at a retired key (espressoAndGold,
// charcoalAndMaple, slateAndCopper, inkAndSage, oxbloodAndBrass, crimsonAndAsh,
// linenAndInk, blackAndOchre), getTheme() will silently fall back to
// DEFAULT_THEME rather than crash — but that's a visible appearance change for a
// live tenant. Corduroy was on espressoAndGold and should move to
// espressoAndBrass (closest match, same backgrounds). Wonder Bar was on
// crimsonAndAsh and should move to blackAndCherry (both dark-dominant with a red
// accent, closest match). Anyone on blackAndOchre should move to
// espressoAndBrass, which is the nearest surviving warm dark. Reassign in
// Settings → device setup → theme before deploying this file.

import { createContext, useContext } from 'react';

export const THEMES = {

  espressoAndBrass: {
    name: "espressoAndBrass",
    displayName: "Espresso & Brass",
    description: "Moody, luxury, steakhouse, cocktail",
    isLight: false,
    // Lightened Nov 2, 2026 (Lindsay chose option C of four). The page was the second-darkest of
    // the dark themes (lightness 6.8; the others 9.5-11.8). Page, buttons, cards and borders all
    // move up together (page 14.4), so cards still stand off the page by the same amount. The
    // brass is brightened to keep its contrast on the lighter page, and everything built from it
    // (accentDim, accentBorder, warning...) follows. Before: bgDeep #0e0b09, bgBase #1a1410,
    // bgSurface #28221e, bgCard #35302d, bgCardHover #433e3b, borderSubtle #211b17, borderMid
    // #312c28, borderStrong #484340, textSecondary #aea6a3, textMuted #807875, textDim #5e5753,
    // textVeryDim #443e3a, brass #b8925a (184,146,90).
    bgDeep: "#1b1613",
    courseBandBg: "#715937",
    bgBase: "#2b231d",
    bgSurface: "#39322c",
    bgCard: "#47413d",
    bgCardHover: "#564f4c",
    bgInput: "rgba(255,255,255,0.05)",
    bgOverlay: "rgba(0,0,0,0.78)",
    accent: "#c9a063",
    accentDim: "rgba(201,160,99,0.14)",
    accentDimSm: "rgba(201,160,99,0.08)",
    accentBorder: "rgba(201,160,99,0.38)",
    accentBorderSm: "rgba(201,160,99,0.22)",
    accentText: "#0e0b09",
    accentSecondary: "#c9a063",
    accentSecondaryDim: "rgba(201,160,99,0.16)",
    accentSecondaryBorder: "rgba(201,160,99,0.42)",
    onDeepText: "#eee5e2",
    onDeepTextDim: "#ded3cd",
    borderSubtle: "#332a25",
    borderMid: "#433d37",
    borderStrong: "#5b5551",
    textPrimary: "#eee5e2",
    textSecondary: "#beb6b2",
    textMuted: "#8d8581",
    textDim: "#6a625e",
    textVeryDim: "#504944",
    // success, error and info are brightened a little so they keep the contrast they had on buttons and
    // cards before the lighter background (success #5a9e6f, error #c85050, info #8fa4d8).
    success: "#66b27d",
    successDim: "rgba(102,178,125,0.12)",
    successBorder: "rgba(102,178,125,0.35)",
    error: "#e25c5c",
    errorDim: "rgba(226,92,92,0.1)",
    errorBorder: "rgba(226,92,92,0.4)",
    warning: "#c9a063",
    warningDim: "rgba(201,160,99,0.12)",
    warningBorder: "rgba(201,160,99,0.35)",
    info: "#a1b8f2",
    infoDim: "rgba(110,130,200,0.14)",
    infoBorder: "rgba(110,130,200,0.42)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.45)",
    shadowStrong: "rgba(0,0,0,0.70)",
    white04: "rgba(255,255,255,0.04)",
    white06: "rgba(255,255,255,0.05)",
    white08: "rgba(255,255,255,0.07)",
    fontSerif: "Georgia, serif",
    fontMono: "monospace",
  },

  parchmentAndMaple: {
    name: "parchmentAndMaple",
    displayName: "Parchment & Maple",
    description: "Classic, elevated, traditional",
    isLight: true,
    bgDeep: "#e4ddce",
    courseBandBg: "#825319",
    bgBase: "#f5f1e8",
    bgSurface: "#faf8f4",
    bgCard: "#e8e4db",
    bgCardHover: "#dbd7cf",
    bgInput: "rgba(32,26,22,0.07)",
    bgOverlay: "rgba(32,26,22,0.55)",
    accent: "#8a581b",
    accentDim: "rgba(138,88,27,0.18)",
    accentDimSm: "rgba(138,88,27,0.12)",
    accentBorder: "rgba(138,88,27,0.55)",
    accentBorderSm: "rgba(138,88,27,0.35)",
    accentText: "#ffffff",
    accentSecondary: "#8a581b",
    accentSecondaryDim: "rgba(138,88,27,0.2)",
    accentSecondaryBorder: "rgba(138,88,27,0.55)",
    onDeepText: "#f5f1e8",
    onDeepTextDim: "#e0d4c3",
    borderSubtle: "#e4e0d7",
    borderMid: "#c6c2ba",
    borderStrong: "#a09b94",
    textPrimary: "#201a16",
    // Darkened Nov 3, 2026 for readability: textDim (help text, hints, "editing:")
    // measured 1.6:1 on bgCard, far under the 4.5:1 guideline for normal text.
    // Same tints, same order; each now measured on the darkest place it sits
    // (white06 inside a card, #d8d4cb): secondary 6.5, muted 5.4, dim 4.6,
    // veryDim 3.0 (deliberately greyed-out text, e.g. an unavailable category).
    // Was: #6b6560 / #95908a / #b9b5ad / #d3cfc6.
    textSecondary: "#484441",
    textMuted: "#53504b",
    textDim: "#5f5a51",
    textVeryDim: "#807763",
    success: "#2f6b4a",
    successDim: "rgba(47,107,74,0.1)",
    successBorder: "rgba(47,107,74,0.3)",
    error: "#a8302e",
    errorDim: "rgba(168,48,46,0.08)",
    errorBorder: "rgba(168,48,46,0.35)",
    warning: "#8a6410",
    warningDim: "rgba(138,100,16,0.12)",
    warningBorder: "rgba(138,100,16,0.35)",
    info: "#3a5ba8",
    infoDim: "rgba(58,91,168,0.12)",
    infoBorder: "rgba(58,91,168,0.38)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.12)",
    shadowStrong: "rgba(0,0,0,0.22)",
    white04: "rgba(32,26,22,0.05)",
    white06: "rgba(32,26,22,0.08)",
    white08: "rgba(32,26,22,0.12)",
    fontSerif: "'Playfair Display', Georgia, serif",
    fontMono: "monospace",
  },

  blackAndCherry: {
    name: "blackAndCherry",
    displayName: "Black & Cherry",
    description: "Modern steakhouse, bold, masculine — dark-dominant, true cherry red accent",
    isLight: false,
    bgDeep: "#111111",
    courseBandBg: "#b2262e",
    bgBase: "#1f1f1f",
    bgSurface: "#2c2c2c",
    bgCard: "#3a3a3a",
    bgCardHover: "#474747",
    bgInput: "rgba(255,255,255,0.05)",
    bgOverlay: "rgba(0,0,0,0.78)",
    accent: "#b3262e",
    accentDim: "rgba(179,38,46,0.14)",
    accentDimSm: "rgba(179,38,46,0.08)",
    accentBorder: "rgba(179,38,46,0.38)",
    accentBorderSm: "rgba(179,38,46,0.22)",
    accentText: "#111111",
    accentSecondary: "#a93e43",
    accentSecondaryDim: "rgba(169,62,67,0.16)",
    accentSecondaryBorder: "rgba(169,62,67,0.42)",
    onDeepText: "#f0e6e5",
    onDeepTextDim: "#e9d1d1",
    borderSubtle: "#262626",
    borderMid: "#353535",
    borderStrong: "#4c4c4c",
    textPrimary: "#f0e6e5",
    textSecondary: "#b1aaaa",
    textMuted: "#837f7e",
    textDim: "#625f5e",
    textVeryDim: "#494747",
    success: "#5a9e6f",
    successDim: "rgba(90,158,111,0.12)",
    successBorder: "rgba(90,158,111,0.35)",
    error: "#c85050",
    errorDim: "rgba(200,80,80,0.1)",
    errorBorder: "rgba(200,80,80,0.4)",
    warning: "#b3262e",
    warningDim: "rgba(179,38,46,0.12)",
    warningBorder: "rgba(179,38,46,0.35)",
    info: "#8fa4d8",
    infoDim: "rgba(110,130,200,0.14)",
    infoBorder: "rgba(110,130,200,0.42)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.45)",
    shadowStrong: "rgba(0,0,0,0.70)",
    white04: "rgba(255,255,255,0.04)",
    white06: "rgba(255,255,255,0.05)",
    white08: "rgba(255,255,255,0.07)",
    fontSerif: "Georgia, serif",
    fontMono: "monospace",
  },

  cobaltAndWhite: {
    name: "cobaltAndWhite",
    displayName: "Cobalt & White",
    description: "Modern, coastal, seafood, energetic",
    isLight: true,
    bgDeep: "#0b2340",
    courseBandBg: "#15609c",
    bgBase: "#ffffff",
    bgSurface: "#ffffff",
    bgCard: "#f1f2f2",
    bgCardHover: "#e3e4e5",
    bgInput: "rgba(23,32,42,0.07)",
    bgOverlay: "rgba(23,32,42,0.55)",
    accent: "#1769aa",
    accentDim: "rgba(23,105,170,0.18)",
    accentDimSm: "rgba(23,105,170,0.12)",
    accentBorder: "rgba(23,105,170,0.55)",
    accentBorderSm: "rgba(23,105,170,0.35)",
    accentText: "#ffffff",
    accentSecondary: "#1769aa",
    accentSecondaryDim: "rgba(23,105,170,0.2)",
    accentSecondaryBorder: "rgba(23,105,170,0.55)",
    onDeepText: "#eaf2fa",
    onDeepTextDim: "#c4d8e9",
    borderSubtle: "#ecedee",
    borderMid: "#ccced0",
    borderStrong: "#a2a6aa",
    textPrimary: "#17202a",
    textSecondary: "#686e75",
    textMuted: "#979b9f",
    textDim: "#bec1c3",
    textVeryDim: "#dadbdd",
    success: "#2f6b4a",
    successDim: "rgba(47,107,74,0.1)",
    successBorder: "rgba(47,107,74,0.3)",
    error: "#a8302e",
    errorDim: "rgba(168,48,46,0.08)",
    errorBorder: "rgba(168,48,46,0.35)",
    warning: "#8a6410",
    warningDim: "rgba(138,100,16,0.12)",
    warningBorder: "rgba(138,100,16,0.35)",
    info: "#3a5ba8",
    infoDim: "rgba(58,91,168,0.12)",
    infoBorder: "rgba(58,91,168,0.38)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.12)",
    shadowStrong: "rgba(0,0,0,0.22)",
    white04: "rgba(23,32,42,0.05)",
    white06: "rgba(23,32,42,0.08)",
    white08: "rgba(23,32,42,0.12)",
    fontSerif: "'Playfair Display', Georgia, serif",
    fontMono: "monospace",
  },

  oliveAndLinen: {
    name: "oliveAndLinen",
    displayName: "Olive & Linen",
    description: "Italian, Mediterranean, fresh, farm-to-table",
    isLight: true,
    bgDeep: "#26332a",
    courseBandBg: "#4e6432",
    bgBase: "#ffffff",
    bgSurface: "#ffffff",
    bgCard: "#f2f2f2",
    bgCardHover: "#e4e5e4",
    bgInput: "rgba(32,35,30,0.07)",
    bgOverlay: "rgba(32,35,30,0.55)",
    accent: "#536b35",
    accentDim: "rgba(83,107,53,0.18)",
    accentDimSm: "rgba(83,107,53,0.12)",
    accentBorder: "rgba(83,107,53,0.55)",
    accentBorderSm: "rgba(83,107,53,0.35)",
    accentText: "#ffffff",
    accentSecondary: "#536b35",
    accentSecondaryDim: "rgba(83,107,53,0.2)",
    accentSecondaryBorder: "rgba(83,107,53,0.55)",
    onDeepText: "#f3f1e8",
    onDeepTextDim: "#d5d7c7",
    borderSubtle: "#ededed",
    borderMid: "#cecfce",
    borderStrong: "#a6a7a5",
    textPrimary: "#20231e",
    textSecondary: "#6e706d",
    textMuted: "#9b9c9a",
    textDim: "#c1c1c0",
    textVeryDim: "#dbdcdb",
    success: "#2f6b4a",
    successDim: "rgba(47,107,74,0.1)",
    successBorder: "rgba(47,107,74,0.3)",
    error: "#a8302e",
    errorDim: "rgba(168,48,46,0.08)",
    errorBorder: "rgba(168,48,46,0.35)",
    warning: "#8a6410",
    warningDim: "rgba(138,100,16,0.12)",
    warningBorder: "rgba(138,100,16,0.35)",
    info: "#3a5ba8",
    infoDim: "rgba(58,91,168,0.12)",
    infoBorder: "rgba(58,91,168,0.38)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.12)",
    shadowStrong: "rgba(0,0,0,0.22)",
    white04: "rgba(32,35,30,0.05)",
    white06: "rgba(32,35,30,0.08)",
    white08: "rgba(32,35,30,0.12)",
    fontSerif: "'Playfair Display', Georgia, serif",
    fontMono: "monospace",
  },

  terracottaAndChalk: {
    name: "terracottaAndChalk",
    displayName: "Terracotta & Chalk",
    description: "Contemporary Italian, Mexican, warm casual",
    isLight: true,
    bgDeep: "#33211c",
    courseBandBg: "#a23c26",
    bgBase: "#faf7f0",
    bgSurface: "#fcfbf8",
    bgCard: "#edeae3",
    bgCardHover: "#e1ddd7",
    bgInput: "rgba(39,33,30,0.07)",
    bgOverlay: "rgba(39,33,30,0.55)",
    accent: "#b8442b",
    accentDim: "rgba(184,68,43,0.18)",
    accentDimSm: "rgba(184,68,43,0.12)",
    accentBorder: "rgba(184,68,43,0.55)",
    accentBorderSm: "rgba(184,68,43,0.35)",
    accentText: "#ffffff",
    accentSecondary: "#b8442b",
    accentSecondaryDim: "rgba(184,68,43,0.2)",
    accentSecondaryBorder: "rgba(184,68,43,0.55)",
    onDeepText: "#f7f0e6",
    onDeepTextDim: "#e9d1c5",
    borderSubtle: "#e9e6df",
    borderMid: "#ccc8c2",
    borderStrong: "#a6a19c",
    textPrimary: "#27211e",
    textSecondary: "#716c68",
    textMuted: "#9b9792",
    textDim: "#bfbbb5",
    textVeryDim: "#d8d5ce",
    success: "#2f6b4a",
    successDim: "rgba(47,107,74,0.1)",
    successBorder: "rgba(47,107,74,0.3)",
    error: "#a8302e",
    errorDim: "rgba(168,48,46,0.08)",
    errorBorder: "rgba(168,48,46,0.35)",
    warning: "#8a6410",
    warningDim: "rgba(138,100,16,0.12)",
    warningBorder: "rgba(138,100,16,0.35)",
    info: "#3a5ba8",
    infoDim: "rgba(58,91,168,0.12)",
    infoBorder: "rgba(58,91,168,0.38)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.12)",
    shadowStrong: "rgba(0,0,0,0.22)",
    white04: "rgba(39,33,30,0.05)",
    white06: "rgba(39,33,30,0.08)",
    white08: "rgba(39,33,30,0.12)",
    fontSerif: "'Playfair Display', Georgia, serif",
    fontMono: "monospace",
  },

  // Converted from the original light "Emerald & Cream" to a dark, jewel-toned
  // palette - the key stays emeraldAndCream (not renamed) so any restaurant
  // already configured with this theme carries straight over to the new look
  // rather than silently losing their selection. Only the displayName and
  // catalogue label change, to "Emerald & Platinum".
  emeraldAndCream: {
    name: "emeraldAndCream",
    displayName: "Emerald & Platinum",
    description: "Moody, upscale, jewel-toned, modern luxury",
    isLight: false,
    bgDeep: "#081511",
    courseBandBg: "#1c6952",
    bgBase: "#10211a",
    bgSurface: "#17281f",
    bgCard: "#1f362a",
    bgCardHover: "#294432",
    bgInput: "rgba(255,255,255,0.05)",
    bgOverlay: "rgba(0,0,0,0.78)",
    accent: "#35c69a",
    accentDim: "rgba(53,198,154,0.14)",
    accentDimSm: "rgba(53,198,154,0.08)",
    accentBorder: "rgba(53,198,154,0.38)",
    accentBorderSm: "rgba(53,198,154,0.22)",
    accentText: "#081511",
    accentSecondary: "#c7ccd1",
    accentSecondaryDim: "rgba(199,204,209,0.16)",
    accentSecondaryBorder: "rgba(199,204,209,0.42)",
    onDeepText: "#eef5f0",
    onDeepTextDim: "#c6dbd2",
    borderSubtle: "#16241d",
    borderMid: "#2a3d32",
    borderStrong: "#3e5447",
    textPrimary: "#eef5f0",
    textSecondary: "#b9c2bb",
    textMuted: "#8a948d",
    textDim: "#5c655e",
    textVeryDim: "#434a44",
    success: "#5a9e6f",
    successDim: "rgba(90,158,111,0.12)",
    successBorder: "rgba(90,158,111,0.35)",
    error: "#c85050",
    errorDim: "rgba(200,80,80,0.1)",
    errorBorder: "rgba(200,80,80,0.4)",
    warning: "#c9a227",
    warningDim: "rgba(201,162,39,0.12)",
    warningBorder: "rgba(201,162,39,0.35)",
    info: "#8fa4d8",
    infoDim: "rgba(110,130,200,0.14)",
    infoBorder: "rgba(110,130,200,0.42)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.45)",
    shadowStrong: "rgba(0,0,0,0.70)",
    white04: "rgba(255,255,255,0.04)",
    white06: "rgba(255,255,255,0.05)",
    white08: "rgba(255,255,255,0.07)",
    fontSerif: "'Playfair Display', Georgia, serif",
    fontMono: "monospace",
  },

  // New 10th theme - pure black and white, inverted from Ink & Paper rather
  // than converting an existing warm theme. "Chalk" instead of "Paper"
  // because a restaurant's own chalkboard specials board is the real-world
  // reference for white-on-black, where "Ink & Paper" was black-on-white.
  onyxAndChalk: {
    name: "onyxAndChalk",
    displayName: "Onyx & Chalk",
    description: "Minimal, graphic, editorial, modern",
    isLight: false,
    bgDeep: "#000000",
    courseBandBg: "#5d5d5d",
    bgBase: "#0a0a0a",
    bgSurface: "#141414",
    bgCard: "#1e1e1e",
    bgCardHover: "#272727",
    bgInput: "rgba(255,255,255,0.05)",
    bgOverlay: "rgba(0,0,0,0.78)",
    accent: "#ffffff",
    accentDim: "rgba(255,255,255,0.14)",
    accentDimSm: "rgba(255,255,255,0.08)",
    accentBorder: "rgba(255,255,255,0.38)",
    accentBorderSm: "rgba(255,255,255,0.22)",
    accentText: "#0a0a0a",
    accentSecondary: "#ffffff",
    accentSecondaryDim: "rgba(255,255,255,0.16)",
    accentSecondaryBorder: "rgba(255,255,255,0.42)",
    onDeepText: "#ffffff",
    onDeepTextDim: "#d5d5d5",
    borderSubtle: "#171717",
    borderMid: "#2a2a2a",
    borderStrong: "#3e3e3e",
    textPrimary: "#f5f5f5",
    textSecondary: "#9a9a9a",
    textMuted: "#707070",
    textDim: "#4a4a4a",
    textVeryDim: "#333333",
    success: "#6bbf85",
    successDim: "rgba(107,191,133,0.12)",
    successBorder: "rgba(107,191,133,0.35)",
    error: "#d16868",
    errorDim: "rgba(209,104,104,0.1)",
    errorBorder: "rgba(209,104,104,0.4)",
    warning: "#d4a24c",
    warningDim: "rgba(212,162,76,0.12)",
    warningBorder: "rgba(212,162,76,0.35)",
    info: "#8fa4d8",
    infoDim: "rgba(110,130,200,0.14)",
    infoBorder: "rgba(110,130,200,0.42)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.5)",
    shadowStrong: "rgba(0,0,0,0.75)",
    white04: "rgba(255,255,255,0.04)",
    white06: "rgba(255,255,255,0.05)",
    white08: "rgba(255,255,255,0.07)",
    fontSerif: "Georgia, serif",
    fontMono: "monospace",
  },

  // Converted from the original steel-grey "Slate & Chrome" to a dark navy and
  // white scheme. The key stays slateAndChrome (not renamed) so any restaurant
  // already configured with it carries straight over to the new look instead of
  // silently losing its selection - only displayName and the catalogue label
  // change, to "Navy & White". The key's name is now a misnomer on purpose:
  // renaming it would orphan every saved setting. Cobalt & White is a separate,
  // light scheme and is untouched.
  //
  // Pure white is the whole accent: there is deliberately no blue accent
  // colour, so the scheme is exactly two colours. The cost is that tappable
  // things are the same colour as body text and stand out by position and
  // weight, not hue - a trade made knowingly for the formal, nautical look.
  slateAndChrome: {
    name: "slateAndChrome",
    displayName: "Navy & White",
    description: "Classic, nautical, formal, coastal seafood, hotel bar",
    isLight: false,
    bgDeep: "#050d1a",
    courseBandBg: "#2b4c7a",
    bgBase: "#0d1b2e",
    bgSurface: "#132641",
    bgCard: "#1a3252",
    bgCardHover: "#21406a",
    bgInput: "rgba(255,255,255,0.05)",
    bgOverlay: "rgba(0,0,0,0.78)",
    accent: "#ffffff",
    accentDim: "rgba(255,255,255,0.10)",
    accentDimSm: "rgba(255,255,255,0.06)",
    accentBorder: "rgba(255,255,255,0.35)",
    accentBorderSm: "rgba(255,255,255,0.20)",
    accentText: "#0d1b2e",
    accentSecondary: "#ffffff",
    accentSecondaryDim: "rgba(255,255,255,0.16)",
    accentSecondaryBorder: "rgba(255,255,255,0.42)",
    onDeepText: "#ffffff",
    onDeepTextDim: "#c4d2e3",
    borderSubtle: "#122238",
    borderMid: "#1f3a5a",
    borderStrong: "#2a4668",
    textPrimary: "#ffffff",
    textSecondary: "#a9b7c6",
    textMuted: "#8396ae",
    textDim: "#6f84a0",
    textVeryDim: "#4d6280",
    success: "#6bbf85",
    successDim: "rgba(107,191,133,0.12)",
    successBorder: "rgba(107,191,133,0.35)",
    error: "#d16868",
    errorDim: "rgba(209,104,104,0.1)",
    errorBorder: "rgba(209,104,104,0.4)",
    warning: "#d4a24c",
    warningDim: "rgba(212,162,76,0.12)",
    warningBorder: "rgba(212,162,76,0.35)",
    info: "#8fa4d8",
    infoDim: "rgba(110,130,200,0.14)",
    infoBorder: "rgba(110,130,200,0.42)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.5)",
    shadowStrong: "rgba(0,0,0,0.75)",
    white04: "rgba(255,255,255,0.04)",
    white06: "rgba(255,255,255,0.05)",
    white08: "rgba(255,255,255,0.07)",
    fontSerif: "'Playfair Display', Georgia, serif",
    fontMono: "monospace",
  },

  inkAndPaper: {
    name: "inkAndPaper",
    displayName: "Ink & Paper",
    description: "Classic crisp monochrome — universal, any room",
    isLight: true,
    bgDeep: "#111111",
    courseBandBg: "#111111",
    bgBase: "#ffffff",
    bgSurface: "#ffffff",
    bgCard: "#f1f1f1",
    bgCardHover: "#e2e2e2",
    bgInput: "rgba(17,17,17,0.07)",
    bgOverlay: "rgba(17,17,17,0.55)",
    accent: "#111111",
    accentDim: "rgba(17,17,17,0.18)",
    accentDimSm: "rgba(17,17,17,0.12)",
    accentBorder: "rgba(17,17,17,0.55)",
    accentBorderSm: "rgba(17,17,17,0.35)",
    accentText: "#ffffff",
    accentSecondary: "#111111",
    accentSecondaryDim: "rgba(17,17,17,0.2)",
    accentSecondaryBorder: "rgba(17,17,17,0.55)",
    onDeepText: "#ffffff",
    onDeepTextDim: "#bdbdbd",
    borderSubtle: "#ececec",
    borderMid: "#cbcbcb",
    borderStrong: "#a0a0a0",
    textPrimary: "#111111",
    textSecondary: "#6b6b6b",
    textMuted: "#949494",
    textDim: "#bcbcbc",
    textVeryDim: "#d9d9d9",
    success: "#2f6b4a",
    successDim: "rgba(47,107,74,0.1)",
    successBorder: "rgba(47,107,74,0.3)",
    error: "#a8302e",
    errorDim: "rgba(168,48,46,0.08)",
    errorBorder: "rgba(168,48,46,0.35)",
    warning: "#8a6410",
    warningDim: "rgba(138,100,16,0.12)",
    warningBorder: "rgba(138,100,16,0.35)",
    info: "#3a5ba8",
    infoDim: "rgba(58,91,168,0.12)",
    infoBorder: "rgba(58,91,168,0.38)",
    labelBackdrop: "#f5ede0",
    shadow: "rgba(0,0,0,0.12)",
    shadowStrong: "rgba(0,0,0,0.22)",
    white04: "rgba(17,17,17,0.05)",
    white06: "rgba(17,17,17,0.08)",
    white08: "rgba(17,17,17,0.12)",
    fontSerif: "'Playfair Display', Georgia, serif",
    fontMono: "monospace",
  },
};

// ─── Semantic colours ─────────────────────────────────────────────────────────
// The re-enrich blue and the approve green moved into the schemes themselves as
// `info*` and `success*`, so a manager screen tints with the restaurant's palette
// like everything else. What is left here is the short list of colours that
// genuinely must NOT follow the scheme.

export const SEMANTIC = {
  qrBackdrop:        "#ffffff",   // a QR needs a light quiet zone to scan
  lightboxBase:      "#1a1a1a",   // full-screen image viewer
  lightboxText:      "#f2efe9",   // caption sits on a scrim, so always light
  lightboxTextDim:   "rgba(242,239,233,0.55)",

  // The logo preview swatches exist to show a logo against a light field and a
  // dark one. Theming them would defeat the entire control.
  previewLight:      "rgba(255,255,255,0.92)",
  previewDark:       "rgba(0,0,0,0.55)",
  previewLightText:  "rgba(0,0,0,0.20)",
  previewDarkText:   "rgba(255,255,255,0.25)",
};

// Used by ErrorBoundary, which cannot read the theme context — the provider may
// be the thing that crashed. Kept in sync with espressoAndBrass by hand, since
// that's now the default/fallback theme.
export const CRASH_SCREEN = {
  bg:           "#0e0b09",
  panel:        "#1a1410",
  border:       "#3e2c1e",
  heading:      "#c85050",
  text:         "#eee5e2",
  mono:         "#b2a095",
  label:        "#7c685d",
  accent:       "#b8925a",
  accentDim:    "rgba(184,146,90,0.14)",
  accentBorder: "rgba(184,146,90,0.38)",
  font:         "Georgia, serif",
};

// Register every scheme here or it will not appear in Settings.
// `preview` is the swatch colour for a picker — bgDeep for dark-dominant schemes,
// bgBase for light-dominant. Note the three pure-white schemes share the same
// preview value; nothing renders it today (SettingsTab uses a plain <select> of
// labels), but if a swatch picker is ever built, those three will need the accent
// as their swatch instead or they will be indistinguishable.
export const THEME_CATALOGUE = [
  // Ordered by colour family, each as a light/dark pair (light first), so
  // flicking through the Settings dropdown or the demo switcher shows a scheme
  // and its counterpart back to back. Family order: green, brown, black and
  // white, blue, red.
  //   green  - Olive & Linen         / Emerald & Platinum
  //   brown  - Parchment & Maple     / Espresso & Brass
  //   mono   - Ink & Paper           / Onyx & Chalk
  //   blue   - Cobalt & White        / Navy & White   (key: slateAndChrome)
  //   red    - Terracotta & Chalk    / Black & Cherry
  // Olive & Linen stays first on purpose: it is the public demo tenant's
  // actual default, so the switcher opens on "1 of 10". New schemes should be
  // added as a pair, next to their counterpart, not appended at the end.
  { value: "oliveAndLinen", label: "Olive & Linen", preview: "#ffffff" },
  { value: "emeraldAndCream", label: "Emerald & Platinum", preview: "#10211a" },
  { value: "parchmentAndMaple", label: "Parchment & Maple", preview: "#f5f1e8" },
  { value: "espressoAndBrass", label: "Espresso & Brass", preview: "#0e0b09" },
  { value: "inkAndPaper", label: "Ink & Paper", preview: "#ffffff" },
  { value: "onyxAndChalk", label: "Onyx & Chalk", preview: "#0a0a0a" },
  { value: "cobaltAndWhite", label: "Cobalt & White", preview: "#ffffff" },
  { value: "slateAndChrome", label: "Navy & White", preview: "#0d1b2e" },
  { value: "terracottaAndChalk", label: "Terracotta & Chalk", preview: "#faf7f0" },
  { value: "blackAndCherry", label: "Black & Cherry", preview: "#111111" },
];

// The platform default (Nov 2, 2026: Parchment & Maple, was Espresso & Brass):
// the admin portal's theme, and what a restaurant shows until its owner picks
// one in Settings. A restaurant with a theme saved is never affected; the
// public demo has its own default (onyxAndChalk, see App.js). A retired theme
// key also falls back to this, so a live tenant on one would change too.
export const DEFAULT_THEME = THEMES.parchmentAndMaple;

export const ThemeContext = createContext(DEFAULT_THEME);
export const useTheme = () => useContext(ThemeContext);

// Falls back rather than returning undefined — a stored theme key that no longer
// exists should show the default, not an unstyled screen.
export function getTheme(key) {
  return THEMES[key] || DEFAULT_THEME;
}
